import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import YesNoQuestion from '../programs/YesNoQuestion';
import { positionHeading } from '@/shared/utils/positionText';
import {
    courseQuestion,
    unansweredRequirements,
} from '@/shared/utils/requirementAnswers';

export const RETRY_ID = 'sap-requirement-retry';
export const courseId = (position) => `sap-requirement-course-${position}`;

const positionIdsOf = (answers) =>
    Array.isArray(answers.positions)
        ? answers.positions.map((p) => p.applicationId)
        : [];

// Radio ids end in -yes (YesNoQuestion), the control the wizard focuses.
const courseControl = (position) => `${courseId(position)}-yes`;

const NOT_MET_NOTICE =
    'You can still apply. The faculty member reviewing reads your resume and decides.';

// The course questions the program asks that are still open. `rules` is the
// requirements endpoint's { assertions } for the chosen program, or null while
// it loads. When it could not be read the questions are unknown, so the step
// stays open until a retry reads them.
export function getErrors(answers, rules) {
    if (rules?.failed) {
        return [{ id: RETRY_ID, label: 'Program requirements' }];
    }
    if (!rules?.assertions?.priorCompletion) return [];
    const positions = positionIdsOf(answers);
    return unansweredRequirements(
        answers.requirementAnswers,
        rules.assertions,
        positions
    )
        .filter((open) => open.rule === 'priorCompletion')
        .map((open) => {
            const row =
                answers.positionRows?.[open.position] ||
                (answers.positions || []).find(
                    (position) => position.applicationId === open.position
                )?.summary;
            return {
                id: courseControl(open.position),
                label: row
                    ? `Prior course question for ${row.subject} ${row.courseNumber}${row.section ? `, section ${row.section}` : ''}`
                    : `Prior course question for choice ${
                          positions.indexOf(open.position) + 1
                      }`,
            };
        });
}

const NOMINEE_NOT_MET_NOTICE =
    'You can still accept. The faculty member who named you reads your answer.';

function Outcome({ value, nominee }) {
    return (
        <div role="status" className="mb-3">
            {value === false && (
                <p className="text-muted mb-0">
                    {nominee ? NOMINEE_NOT_MET_NOTICE : NOT_MET_NOTICE}
                </p>
            )}
        </div>
    );
}

/**
 * The program's course requirement as a yes/no question the student answers
 * about themself, one per chosen position: "Have you completed <course> (or
 * its equivalent)?". A program that does not ask it shows nothing. A No warns
 * and never blocks the application: the resume carries the detail and the
 * program decides. No GPA and no grade is entered or stored.
 * `context.positionRows` names the chosen positions' courses.
 */
function RequirementAnswers({ answers, onChange, errorIds, context }) {
    const assertions = context?.programRules?.assertions;
    const nominee = context?.nominee === true;
    if (context?.programRules?.failed) {
        return (
            <Alert variant="danger" role="alert">
                <p className="mb-2">
                    We could not load this program&apos;s requirements, so we
                    cannot ask you about them yet.
                </p>
                <Button
                    id={RETRY_ID}
                    variant="danger"
                    size="sm"
                    onClick={context.retryRules}
                >
                    Try again
                </Button>
            </Alert>
        );
    }
    if (!assertions?.priorCompletion) return null;
    const current = answers.requirementAnswers;
    const positions = answers.positions || [];
    const set = (patch) =>
        onChange('requirementAnswers', { ...current, ...patch });

    return (
        <div>
            <h2 className="h5 mb-2">Course question</h2>
            <p className="text-muted small">
                {nominee
                    ? 'This question is information for the faculty member who named you. It is not a requirement: you can accept whether your answer is yes or no, and it is not checked against your record.'
                    : 'This question is information for the faculty member who reviews your application. It is not a requirement: you can apply whether your answer is yes or no, and it is not checked against your record.'}
            </p>

            {assertions.priorCompletion &&
                positions.map((position, index) => {
                    const row =
                        context.positionRows?.[position.applicationId] ||
                        position.summary;
                    const label = row
                        ? `${row.subject} ${row.courseNumber}`
                        : `the course for choice ${index + 1}`;
                    const detail = row ? positionHeading(row) : null;
                    const id = courseId(position.applicationId);
                    return (
                        <div key={position.applicationId}>
                            {detail && (
                                <p className="small text-muted mb-1">
                                    {detail}
                                </p>
                            )}
                            <YesNoQuestion
                                id={id}
                                legend={courseQuestion(label)}
                                value={
                                    current.courses[position.applicationId] ??
                                    null
                                }
                                onChange={(value) =>
                                    set({
                                        courses: {
                                            ...current.courses,
                                            [position.applicationId]: value,
                                        },
                                    })
                                }
                                invalid={errorIds?.has(
                                    courseControl(position.applicationId)
                                )}
                                message="Answer whether you have completed this course."
                            />
                            <Outcome
                                value={
                                    current.courses[position.applicationId] ??
                                    null
                                }
                                nominee={nominee}
                            />
                        </div>
                    );
                })}
        </div>
    );
}

export default RequirementAnswers;
